import React from 'react'
import { useTranslation } from 'react-i18next'
import { useField, useFormikContext } from 'formik'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertTriangle } from 'lucide-react'
import FormikInput from '../../../components/form/FormikInput'

export interface SubmittalFormValues {
  displayNumber?: string
  title: string
  description: string
  companyName: string
  responsibleId: string
  dueDate: string | null
  specSectionId: string
  categoryId: string
  subcategoryId: string
  disciplineId: string
  packageId: string
}

interface SubmittalFormProps {
  lookups: any[]
  members: any[]
  companySuggestions: string[]
  /** Number field: the edit dialog always shows it; the create/copy dialog shows it as optional. */
  showDisplayNumber?: boolean
  /** Optional number (create/copy): blank means the server assigns the next project number. */
  displayNumberOptional?: boolean
}

/**
 * Plain shadcn Input + native <datalist> — FormikInput's InputField has no
 * datalist support, and the brief calls for suggestions-as-you-type rather
 * than a closed Select (company is free text on the Submittal model).
 */
const CompanyField: React.FC<{ suggestions: string[] }> = ({ suggestions }) => {
  const { t } = useTranslation()
  const [field, , helpers] = useField('companyName')
  return (
    <div className="space-y-2">
      <Label htmlFor="submittal-company">{t('Submittals_field_company')}</Label>
      <Input
        id="submittal-company"
        name={field.name}
        list="submittal-company-suggestions"
        value={field.value}
        onChange={(e) => helpers.setValue(e.target.value)}
        onBlur={field.onBlur}
        placeholder={t('Submittals_field_company_placeholder')}
        autoComplete="off"
      />
      <datalist id="submittal-company-suggestions">
        {suggestions.map((company) => (
          <option key={company} value={company} />
        ))}
      </datalist>
    </div>
  )
}

/**
 * Shared field body for SubmittalCreateDialog and the edit dialog in
 * detail/SubmittalFieldsCard.tsx. Must render inside a Formik<SubmittalFormValues>
 * context — reads `status.submitError` from it directly so both callers don't
 * have to thread it through as a prop.
 */
const SubmittalForm: React.FC<SubmittalFormProps> = ({
  lookups,
  members,
  companySuggestions,
  showDisplayNumber = false,
  displayNumberOptional = false,
}) => {
  const { t } = useTranslation()
  const { status } = useFormikContext<SubmittalFormValues>()
  const byKind = (kind: string) => lookups.filter((l: any) => l.kind === kind)

  return (
    <div className="space-y-4">
      {status?.submitError && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {status.submitError}
        </div>
      )}

      {showDisplayNumber && (
        <div className="space-y-1">
          <FormikInput type="text" name="displayNumber" label={t('Submittals_field_display_number')} />
          <p className="text-xs text-muted-foreground">
            {t(displayNumberOptional ? 'Submittals_number_auto_hint' : 'Submittals_number_hint')}
          </p>
        </div>
      )}

      <FormikInput
        type="text"
        name="title"
        label={t('Submittals_field_title')}
        placeholder={t('Submittals_field_title_placeholder')}
      />
      <FormikInput
        type="textarea"
        name="description"
        label={t('Submittals_field_description')}
        placeholder={t('Submittals_field_description_placeholder')}
      />
      <CompanyField suggestions={companySuggestions} />

      <div className="grid grid-cols-2 gap-4">
        <FormikInput
          type="select"
          name="responsibleId"
          label={t('Submittals_field_responsible')}
          options={members}
          labelField="name"
          valueField="id"
        />
        <FormikInput type="datetime" name="dueDate" label={t('Submittals_field_due_date')} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormikInput
          type="select"
          name="specSectionId"
          label={t('Submittals_field_spec_section')}
          options={byKind('SPEC_SECTION')}
          labelField="value"
          valueField="id"
        />
        <FormikInput
          type="select"
          name="categoryId"
          label={t('Submittals_field_category')}
          options={byKind('CATEGORY')}
          labelField="value"
          valueField="id"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormikInput
          type="select"
          name="subcategoryId"
          label={t('Submittals_field_subcategory')}
          options={byKind('SUBCATEGORY')}
          labelField="value"
          valueField="id"
        />
        <FormikInput
          type="select"
          name="disciplineId"
          label={t('Submittals_field_discipline')}
          options={byKind('DISCIPLINE')}
          labelField="value"
          valueField="id"
        />
      </div>

      <FormikInput
        type="select"
        name="packageId"
        label={t('Submittals_field_package')}
        options={byKind('PACKAGE')}
        labelField="value"
        valueField="id"
      />
    </div>
  )
}

export default SubmittalForm
